import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { StsConfigHttpLoader } from 'angular-auth-oidc-client';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { AUTH_CONFIGURATION_PATH } from './auth-config.consts';
import { authConfig } from './auth-config';
import { OidcConfiguration, RuntimeOidcConfiguration } from './auth-config.model';
import { mergeRuntimeAuthConfig } from './auth-config.utils';

/**
 * The OIDC settings the application is actually running with.
 *
 * The library keeps its own copy, but the app needs two fields it does not
 * carry — the client id and secret it presents at the token endpoint — and
 * reading them off the compiled-in constant would mean every environment
 * authenticating as dev.
 */
@Injectable({ providedIn: 'root' })
export class AuthConfigStore {
  /** The compiled defaults stand in until the deployed file lands. */
  private config: OidcConfiguration = authConfig;

  current(): OidcConfiguration {
    return this.config;
  }

  adopt(config: OidcConfiguration): void {
    this.config = config;
  }
}

/**
 * Loads the environment's OIDC settings before the library starts up.
 *
 * A missing or unreadable file falls back to the compiled defaults rather than
 * leaving the app unable to log in at all: locally there is nothing mounted,
 * and a deployment that lost its Secret is better off saying so at the identity
 * provider than at a blank page.
 * @returns {StsConfigHttpLoader} The loader `provideAuth` takes.
 */
export const authConfigLoader = (): StsConfigHttpLoader => {
  // Straight to the backend, past the interceptors: `jwtInterceptor` asks the
  // authentication service for a token, that service needs the very settings
  // being fetched here, and asking for them through it is a circle the
  // injector cannot close — the application never starts.
  const http = new HttpClient(inject(HttpBackend));
  const store = inject(AuthConfigStore);

  const config$: Observable<OidcConfiguration> = http
    .get<RuntimeOidcConfiguration>(AUTH_CONFIGURATION_PATH)
    .pipe(
      map((runtime): OidcConfiguration => mergeRuntimeAuthConfig(authConfig, runtime)),
      catchError((): Observable<OidcConfiguration> => {
        console.warn('[AuthConfig] Deployed OIDC settings not found, using the built-in ones.');
        return of(authConfig);
      }),
      tap((config): void => store.adopt(config)),
    );

  return new StsConfigHttpLoader(config$);
};
